import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components/native';
import { H2, H3 } from '../../components/Typography';
import Spacer from '../../components/Spacer';
import Button from '../../components/Button';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/core';
import { StackNavigationProp } from '@react-navigation/stack';
import { useUserContext } from '../../context/UserContext';
import { QualificationParamList } from '../../web-navigation/QualificationNavigator';
import api from '../../api/api';
import environments from '../../environments';
import Colors from '../../constants/Colors';
import { useDeleteUserWithNoPassword } from '../../hooks/user/useDeleteUserWithNoPassword';
import HtmlWebView from '../../components/HtmlWebView';
import ActivityIndicatorScreen from '../../components/ActivityIndicatorScreen';
import elementIDs from '../../../elementIDs';

const StyledQualificationCompleteContainer = styled.ScrollView.attrs(() => ({
  contentContainerStyle: {
    flexGrow: 1,
    alignItems: 'center'
  }
}))`
  background-color: ${Colors.background};
  padding: 0px 12px;
`;

const StyledExternalQualificationContainer = styled.View`
  flex: 1;
  align-items: center;
`;

const StyledExternalQualificationView = styled.ScrollView.attrs(() => ({
  contentContainerStyle: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center'
  }
}))`
  width: 100%;
`;

const StyledCompletedMessageView = styled.View`
  width: 100%;
  padding: 0 16px;
  justify-content: center;
  max-width: 475px;
`;

const StyledImageBackground = styled.ImageBackground`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const StyledButtonContainer = styled.View`
  width: 100%;
  padding: 0 0 16px 0;
`;

const QualificationCompleteScreen: React.FC = () => {
  const navigation = useNavigation<StackNavigationProp<any>>();
  const { user, accessToken, studyParticipants, updateUserSession } =
    useUserContext();
  const route =
    useRoute<RouteProp<QualificationParamList, 'QualificationComplete'>>();
  const { displayNotQualified, message, studyParticipantId, fromJoinScreen } =
    route.params || {};
  const { deletedUser, deleteUser } = useDeleteUserWithNoPassword(user?.id);
  const [userSessionUpdated, setUserSessionUpdated] = useState(false);

  const { existingUser, isPublicQualification, qualified } = useMemo(() => {
    const studyParticipant = studyParticipants?.find(
      sp => sp.id == studyParticipantId
    );
    return {
      existingUser: user?.firstName && user?.lastName,
      studyParticipant,
      isPublicQualification: studyParticipant?.study?.isPublic,
      qualified: Boolean(studyParticipant?.qualified)
    };
  }, [user?.firstName, user?.lastName, studyParticipants, studyParticipantId]);

  const accountSetupUrl = useMemo(() => {
    if (!user?.email) return '';
    return `${environments.webUrl}/account-setup?token=${accessToken}`;
  }, [user?.email, accessToken]);

  const handleAccountDelete = useCallback(async () => {
    api.removeAccessToken();
    window.open(`${window.location.origin}/intro`, '_self');
  }, []);

  useEffect(() => {
    if (deletedUser || !user) {
      handleAccountDelete();
    }
  }, [deletedUser, handleAccountDelete, user]);

  // Update user session in order to avoid stale `studyParticipants` value
  // upon navigating to this screen
  const updateSession = useCallback(async () => {
    await updateUserSession();
    setUserSessionUpdated(true);
  }, [updateUserSession]);

  useEffect(() => {
    updateSession();
  }, [updateSession]);

  if (!userSessionUpdated) {
    return <ActivityIndicatorScreen />;
  }

  return !user || (existingUser && fromJoinScreen) ? (
    existingUser && !displayNotQualified ? (
      <></>
    ) : (
      <StyledQualificationCompleteContainer>
        <StyledCompletedMessageView>
          <Spacer size={2} />
          <H2 center color='dark'>
            {displayNotQualified ? 'Not Qualified' : 'Qualified'}
          </H2>
          <Spacer size={3} />
          {message && (
            <HtmlWebView bodyColor={Colors.darkText} html={message} />
          )}
          {displayNotQualified && (
            <>
              <Spacer size={4} />
              <Button
                testID={elementIDs.qualificationCompleteScreenButtonID}
                onPress={() => {
                  navigation.reset({
                    index: 0,
                    routes: [{ name: 'DiscoverNav' }]
                  });
                }}
              >
                Check Out Other Studies
              </Button>
            </>
          )}
        </StyledCompletedMessageView>
      </StyledQualificationCompleteContainer>
    )
  ) : (
    <StyledExternalQualificationContainer>
      <StyledImageBackground
        resizeMode='stretch'
        source={require('../../assets/intro/background.png')}
      />
      <StyledExternalQualificationView>
        <StyledCompletedMessageView>
          {message && (
            <>
              <HtmlWebView
                bodyColor={Colors.darkText}
                html={`<body style="margin: 0; background-color: transparent; padding: 0;"><div style="position:relative;" >${message}</div></body>`}
              />
              <Spacer size={5} />
            </>
          )}
          {!existingUser ? (
            qualified && !isPublicQualification ? (
              <></>
            ) : (
              <>
                {qualified ? (
                  <H2 color='dark'>Create your account now?</H2>
                ) : (
                  <H3 color='dark'>
                    You may be able to participate in other research in the
                    future. Would you like to create an account?
                  </H3>
                )}
                <Spacer size={4} />
                <StyledButtonContainer>
                  <Button
                    onPress={() => {
                      window.open(
                        accountSetupUrl +
                          (qualified ? '' : '&sendConfirmEmail=true'),
                        '_self'
                      );
                    }}
                  >
                    Yes
                  </Button>
                  <Spacer size='medium' />
                  <Button
                    onPress={() => {
                      if (qualified) {
                        window.open(accountSetupUrl + '&skip=true', '_self');
                      } else {
                        deleteUser();
                      }
                    }}
                    outlined
                  >
                    No
                  </Button>
                </StyledButtonContainer>
              </>
            )
          ) : (
            <StyledButtonContainer>
              <Button
                onPress={() =>
                  window.open(`${environments.webUrl}/open-app`, '_self')
                }
              >
                Download the App
              </Button>
            </StyledButtonContainer>
          )}
        </StyledCompletedMessageView>
      </StyledExternalQualificationView>
    </StyledExternalQualificationContainer>
  );
};

export default QualificationCompleteScreen;
